import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Alert, Badge, Tooltip, Typography, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DateTime } from 'luxon'
import styled from 'styled-components'

import { colours, sizes } from '@qflow/theme'

import { HorizontalDivider } from 'portal/components/Navigation/Main/Toolbar/Divider'
import { DateFormatFull } from 'portal/lib/functions'
import {
  ColourButton,
  Flex,
  icons,
  SmallSpinner,
  Spacer,
  Spinner
} from 'portal/lib/primitives'
import {
  useBeamerBarHeight,
  useModuleIds,
  useUserAccountRole
} from 'portal/lib/hooks'
import { useMixpanelEvents } from './mixpanel'
import { ProjectNameFilterDropdown } from './ProjectNameFilterDropdown'
import { ReportEventCell } from './ReportEventCell'
import { ReportTypePanel } from './ReportTypePanel'
import { useReportsLoader } from './useReportsLoader'
import { Report } from 'portal/store/insights/types'

import './ReportsTable.scss'

const { Title, Text } = Typography
const ACCOUNT_ALL_PROJECTS_FILTER = '__ACCOUNT_ALL_PROJECTS__'

export function ReportsLog() {
  const [showTable, setShowTable] = useState(false)
  const [reportTypeFilter, setReportTypeFilter] = useState<string[] | null>(
    null
  )
  const [projectNameFilter, setProjectNameFilter] = useState<string[] | null>(
    null
  )

  const projectTimeZone = useSelector(
    (state: any) => state.projectDetails.modules?.timeZone
  )

  const { accountId } = useModuleIds()
  const { isAdmin: isAccountAdmin } = useUserAccountRole(accountId)

  const { loading, error, items, showTotalRecords } = useReportsLoader()

  const tableId = 'ReportsTable'

  const { trackOpen } = useMixpanelEvents()

  useEffect(() => {
    trackOpen()
  }, [trackOpen])

  const formattedDisplayItems = useMemo(() => [...items], [items])
  const projectNameFilters = useMemo(
    () => [
      // Account-wide reports are only filterable as a group for Account Admins
      ...(isAccountAdmin
        ? [
            {
              text: 'Account - all projects',
              value: ACCOUNT_ALL_PROJECTS_FILTER
            }
          ]
        : []),
      ...Array.from(
        new Set(
          formattedDisplayItems
            .map(item => item.projectName)
            .filter((projectName): projectName is string =>
              Boolean(projectName)
            )
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map(projectName => ({
          text: projectName,
          value: projectName
        }))
    ],
    [formattedDisplayItems, isAccountAdmin]
  )

  // Find the most recent report for Executive Summary panel
  const latestExecutiveSummary = formattedDisplayItems.reduce<Report | null>(
    (latest, item) =>
      item.issued && (!latest || item.issued > latest.issued) ? item : latest,
    null
  )

  const handleExecutiveSummaryClick = () => {
    setShowTable(true)
    setReportTypeFilter(['Executive Summary'])
  }

  const handleBackToGrid = () => {
    setShowTable(false)
    setReportTypeFilter(null)
    setProjectNameFilter(null)
  }

  const activeFilterCount =
    (reportTypeFilter && reportTypeFilter.length > 0 ? 1 : 0) +
    (projectNameFilter && projectNameFilter.length > 0 ? 1 : 0)

  const allFiltersEmpty = activeFilterCount === 0

  const handleResetFilters = () => {
    setReportTypeFilter(null)
    setProjectNameFilter(null)
  }

  const columns = useMemo<ColumnsType<Report>>(() => {
    return [
      {
        title: 'Period',
        dataIndex: 'period',
        className: 'period',
        width: '10rem',
        sorter: (a, b) => {
          return a.period.localeCompare(b.period)
        },
        sortDirections: ['descend' as const, 'ascend' as const],
        render: (text: string) => {
          return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
            {
              timeZone: projectTimeZone,
              year: 'numeric',
              month: 'long'
            },
            { locale: Intl.DateTimeFormat().resolvedOptions().locale }
          )
        }
      },
      {
        title: 'Date Issued',
        dataIndex: 'issued',
        className: 'issued',
        width: '14rem',
        sorter: (a, b) => {
          return a.issued.localeCompare(b.issued)
        },
        sortDirections: ['descend' as const, 'ascend' as const],
        defaultSortOrder: 'descend' as const,
        render: (text: string) => {
          return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
            DateFormatFull(projectTimeZone),
            { locale: Intl.DateTimeFormat().resolvedOptions().locale }
          )
        }
      },
      {
        title: 'Report Type',
        dataIndex: 'reportType',
        className: 'report-type',
        width: '11rem',
        filteredValue: reportTypeFilter,
        onFilter: (value, record) => {
          return record.reportType === value
        },
        filters: [
          {
            text: 'Executive Summary',
            value: 'Executive Summary'
          }
        ]
      },
      {
        title: 'Project Name',
        dataIndex: 'projectName',
        className: 'project-name',
        filteredValue: projectNameFilter,
        sorter: (a, b) => {
          return (a.projectName || '').localeCompare(b.projectName || '')
        },
        sortDirections: ['descend' as const, 'ascend' as const],
        render: (text: string | null | undefined) =>
          text ? text : 'Account - all projects',
        onFilter: (value, record) => {
          return value === ACCOUNT_ALL_PROJECTS_FILTER
            ? !record.projectName
            : record.projectName === value
        },
        // Drives onFilter/typing only - filterDropdown below renders the options
        filters: projectNameFilters,
        filterDropdown: props => (
          <ProjectNameFilterDropdown {...props} filters={projectNameFilters} />
        )
      },
      {
        title: (
          <Tooltip
            title={
              allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
            }
          >
            <ResetFiltersButton
              aria-label={
                allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
              }
              disabled={allFiltersEmpty}
              onClick={handleResetFilters}
            >
              <Badge
                count={allFiltersEmpty ? 0 : activeFilterCount}
                offset={[-1, 1]}
                color={colours.TEAL}
                styles={{
                  root: {
                    color: 'inherit'
                  }
                }}
              >
                <icons.ResetFilters size="22px" />
              </Badge>
            </ResetFiltersButton>
          </Tooltip>
        ),
        dataIndex: 'readSASUrl',
        className: 'links',
        align: 'right' as const,
        width: '9rem',
        render: (text, record) => {
          return <ReportEventCell item={record} />
        }
      }
    ]
  }, [
    activeFilterCount,
    allFiltersEmpty,
    projectNameFilter,
    projectNameFilters,
    projectTimeZone,
    reportTypeFilter
  ])

  const style: React.CSSProperties = { width: '100%' }
  const scrollHeight = `calc(100vh - ${340 + useBeamerBarHeight()}px)`

  const scrollToTop = () => {
    const tableContent = document.querySelector('.ant-table-body')
    tableContent?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  return (
    <>
      <Flex.Row
        style={{
          width: '99%',
          alignItems: 'baseline',
          flex: '0 0 auto',
          minHeight: '35px'
        }}
      >
        <Title level={2} id="Heading" style={{ marginBottom: '0' }}>
          Reports
        </Title>
        <Flex.By style={{ flex: '1 1 auto' }} />
      </Flex.Row>
      <HorizontalDivider style={{ width: '99%' }} />
      {error && (
        <Text type="danger">
          There was a problem loading this page, please try again
        </Text>
      )}

      {/* Grid View - Default */}
      {!showTable && !error && (
        <>
          {loading && <Spinner />}
          {!loading && (
            <ContentWrapper>
              <ReportTypesGrid>
                <ReportTypePanel
                  title="Executive Summary"
                  subtitle="Monthly"
                  latestPeriod={latestExecutiveSummary?.period}
                  latestIssued={latestExecutiveSummary?.issued}
                  projectTimeZone={projectTimeZone}
                  onClick={
                    items.length === 0 ? undefined : handleExecutiveSummaryClick
                  }
                  disabled={items.length === 0}
                  showComingSoon={false}
                  info="A monthly, downloadable PDF report giving you a complete snapshot of your account portfolio — records, compliance, trends, and more."
                />
              </ReportTypesGrid>

              {items.length === 0 && (
                <Alert
                  message="No reports are currently available for this account."
                  description={
                    <>
                      Please speak with your Customer Success Manager to find
                      out more information, or contact us at{' '}
                      <a href="mailto:support@qualisflow.com">
                        support@qualisflow.com
                      </a>
                    </>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: '1rem' }}
                />
              )}
            </ContentWrapper>
          )}
        </>
      )}

      {/* Table View - Executive Summary */}
      {showTable && !error && (
        <>
          <BackButtonRow>
            <ColourButton.BasicBorderless onClick={handleBackToGrid}>
              <icons.ChevronRight size={sizes.SMALL} rotate={180} />
              <Spacer.Fine />
              Reports summary
            </ColourButton.BasicBorderless>
          </BackButtonRow>
          {(!loading || formattedDisplayItems.length > 30) && (
            <>
              <ContentWrapper style={{ marginTop: '0.5rem' }}>
                <Table
                  id={tableId}
                  columns={columns}
                  dataSource={formattedDisplayItems}
                  rowKey={record =>
                    `${record.accountId}-${record.period}-${record.reportName}`
                  }
                  pagination={false}
                  scroll={{ y: scrollHeight }}
                  sticky
                  style={style}
                  rowClassName={() => 'event-row-open-light'}
                  onChange={(pagination, filters) => {
                    setReportTypeFilter(filters.reportType as string[] | null)
                    setProjectNameFilter(filters.projectName as string[] | null)
                  }}
                  loading={{
                    spinning: loading,
                    indicator: (
                      <div>
                        <SmallSpinner />
                      </div>
                    )
                  }}
                  locale={{
                    emptyText: (
                      <ZeroRecordsFooter>
                        <icons.ManageSearch />
                        <div>No reports found.</div>
                      </ZeroRecordsFooter>
                    )
                  }}
                  onRow={
                    (() => ({
                      'data-testid': 'ReportsTableRow'
                    })) as any
                  }
                  footer={() => {
                    return null
                  }}
                />
              </ContentWrapper>
              <TableFooter>
                {showTotalRecords()}
                <ButtonContainer>
                  <BTTButton id="BTTButton" onClick={scrollToTop}>
                    Back to top
                    <Spacer.Fine />
                    <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
                  </BTTButton>
                </ButtonContainer>
              </TableFooter>
            </>
          )}
          {loading && formattedDisplayItems.length < 31 && <Spinner />}
        </>
      )}
    </>
  )
}

const ContentWrapper = styled.div`
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.5rem 1.25rem 1.25rem 1.25rem;
  margin-top: 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`

const ZeroRecordsFooter = styled.div`
  display: flex;
  justify-content: center;
`

const TableFooter = styled('div')`
  position: relative;
  width: 99%;
`

const ButtonContainer = styled('div')`
  position: absolute;
  right: 0;
  top: 0.75rem;
`

const BTTButton = styled(ColourButton.Basic)`
  line-height: 1;
  width: auto;
`

const ReportTypesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  width: 99%;
`

const BackButtonRow = styled.div`
  margin-top: -1rem;
`

const ResetFiltersButton = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;
  margin-left: auto;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
